import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Modal } from "./UI";
import "../../ucat-styles.css";
import {
  UserAnswer,
  Question,
  ExamSection,
  AnswerType,
} from "../../Components/sample data/Type";
import { Flag, MessageCircle, AlertTriangle, Info, PenLine, LogOut, CheckCircle, XCircle, GripHorizontal, AlignVerticalJustifyCenter } from "lucide-react";
import { useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { ReactComponent as InfoSvg } from "../../Assests/icons8-info (1).svg";
import CircularProgress from "@mui/material/CircularProgress";

import InfoIcon from "../../Assests/info_i.png"
import AlertIcon from "../../Assests/alert_icon.png"
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// Register necessary modules
const FontSize = ReactQuill.Quill.import("formats/size");
FontSize.whitelist = ["small", false, "large", "huge"];
ReactQuill.Quill.register(FontSize, true);

const Align = ReactQuill.Quill.import("formats/align");
Quill.register(Align, true);

const modules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
  ],
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

const formats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "list",
  "bullet",
  "indent",
  "direction",
  "blockquote",
  "align",
];

interface WritingQuestionProps {
  value: string;
  onChange: (value: string) => void;
  isReviewMode?: boolean;
}

export const WritingQuestion: React.FC<WritingQuestionProps> = ({
  value,
  onChange,
  isReviewMode = false,
}) => {
  return (
    <ReactQuill
      className="custom-quill"
      style={{
        width: "98%",
        padding: "20px 0px 20px 0px",
        height: "50vh",
        borderRadius: "20px",
        marginLeft: "10px",
      }}
      theme="snow"
      value={value || ""}
      onChange={onChange}
      modules={modules}
      formats={formats}
      tabIndex={0}
      placeholder={isReviewMode ? "No answer provided" : "Write something here..."}
      preserveWhitespace
      readOnly={isReviewMode}
    />
  );
};

// --- TI-108 Style Calculator ---

// --- Question Navigator (Table Style) ---
interface NavigatorProps {
  sections: ExamSection[];
  currentSectionIndex: number;
  currentQuestionIndex: number;
  answers: Record<string, UserAnswer>;
  onJump: (sectionIdx: number, questionIdx: number) => void;
  onClose: () => void;
}

const NavigatorTable: React.FC<NavigatorProps> = ({
  sections,
  currentSectionIndex,
  currentQuestionIndex,
  answers,
  onJump,
  onClose,
}) => {
  return (
    <div className="flex flex-col h-full bg-white text-sm">
      <div className="bg-[#006DAA] text-white p-2 text-[16px] font-normal flex justify-between items-center">
        {/* <span className="text-[16px] font-normal f-tahoma">Question Bank - {sections[currentSectionIndex].title}</span> */}
      </div>

      <div className="overflow-auto ucat-scrollbar border border-gray-300 max-h-[60vh]">
        <table className="w-full text-left border-collapse">
          <thead className="bg-[#4678BE] text-white  text-[16px] sticky top-0">
            <tr className="border-b border-blue-400 f-tahoma">
              <th className="bg-[#4678BE] p-2 border-r text-[16px] border-blue-400 w-1/4 font-[700]">Question #</th>
              {/* <th className="bg-[#4678BE] p-2 border-r text-[16px] border-blue-400 w-1/4 font-[700]">Concept</th> */}
              <th className="bg-[#4678BE] p-2 border-r text-[16px] border-blue-400 w-1/4 font-[700]">Status</th>
              <th className="bg-[#4678BE] p-2 border-r text-[16px] border-blue-400 w-1/4 font-[700]">Flagged - Review</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section, sIdx) => (
              <React.Fragment key={section.id}>
                {/* Section Header Row */}
                <tr className="bg-[#4678BE] border-b border-gray-300 hover:bg-[#FFFF00]/80 ">
                  <td colSpan={4} className="p-1 px-2 text-[14px] font-semibold text-white f-tahoma">
                    Section {sIdx + 1} - {section.title}
                  </td>
                </tr>

                {/* Questions */}
                {section.questions.map((q, qIdx) => {
                  const ans = answers[q.question_id];
                  const isAnswered =
                    (typeof ans?.selectedOptionIndex === "number" && ans.selectedOptionIndex >= 0) ||
                    (ans?.dragDropAnswers && Object.values(ans.dragDropAnswers).some(val => val !== null));
                  const isViewed = ans?.isViewed || false;
                  const isFlagged = ans?.isFlagged;
                  const isCurrent =
                    sIdx === currentSectionIndex &&
                    qIdx === currentQuestionIndex;

                  return (
                    <tr
                      key={q.question_id}
                      onClick={() => {
                        onJump(sIdx, qIdx);
                        onClose();
                      }}
                      className={`cursor-pointer border-b  text-[16px]  border-gray-200 hover:!bg-[#FFFF00] f-tahoma 
  ${isCurrent ? "bg-blue-100 " : "bg-white"}`}

                    >
                      <td className=" text-[16px] font-600 p-2 border-r border-gray-200">
                        Question {q.displayIndex || qIdx + 1}
                      </td>
                      {/* <td className=" text-[14px] font-600 p-2 border-r border-gray-200">
                        {q.subtype_name || section.title}
                      </td> */}
                      <td
                        className={`text-[16px] font-600 p-2 border-r border-gray-200 ${!isAnswered ? "text-red-600" : ""
                          }`}
                      >
                        {isAnswered ? "Complete" : (isViewed ? "Incomplete" : "Unseen")}
                      </td>
                      <td className="p-2">
                        {isFlagged && (
                          <Flag
                            size={14}
                            className="text-[16px] font-600 text-orange-600 fill-current inline mr-1"
                          />
                        )}
                        {isFlagged && "Flagged"}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-2 bg-gray-100 text-xs text-gray-600 border-t">
        Click on a question to navigate to it.
      </div>
    </div >
  );
};

// --- Navigator Modal Wrapper ---
interface NavigatorModalProps extends NavigatorProps {
  isOpen: boolean;
}

export const NavigatorModal: React.FC<NavigatorModalProps> = (props) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title="Navigator - select a question to go to it"
      width="max-w-4xl"
    >
      <NavigatorTable {...props} />
    </Modal>
  );
};

// --- True/False Question (Yes/No style) ---
interface TrueFalseQuestionProps {
  question: Question;
  selectedOptionIndex: number | null;
  onOptionSelect: (index: number) => void;
  isReviewMode?: boolean;
}

export const TrueFalseQuestion: React.FC<TrueFalseQuestionProps> = ({
  question,
  selectedOptionIndex,
  onOptionSelect,
  isReviewMode = false,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        {question.options.map((option, idx) => {
          const isSelected = selectedOptionIndex === idx;
          return (
            <button
              key={option.option_id}
              disabled={isReviewMode}
              onClick={() => onOptionSelect(idx)}
              className={`flex items-center justify-between p-4 border rounded-lg transition-all ${isSelected
                ? "border-[#005a9c] bg-[#e6f0f7] ring-1 ring-[#005a9c]"
                : "border-gray-200 bg-white hover:border-gray-300"
                }`}
            >
              <span
                className="text-[16px] font-medium text-gray-800"
                dangerouslySetInnerHTML={{ __html: option.option_text || (idx === 0 ? "True" : "False") }}
              />
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-[#005a9c] bg-[#005a9c]" : "border-gray-300"
                  }`}
              >
                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Drag and Drop Question ---
interface DragDropQuestionProps {
  question: Question;
  dragDropAnswers: Record<number, string | null>;
  onAnswerChange: (itemId: number, bucketName: string | null, isExclusive?: boolean) => void;
  isReviewMode?: boolean;
  isLocked?: boolean;
  onLockedClick?: () => void;
}

export const DragDropQuestion: React.FC<DragDropQuestionProps> = ({
  question,
  dragDropAnswers,
  onAnswerChange,
  isReviewMode = false,
  isLocked = false,
  onLockedClick,
}) => {
  const items = question.drag_drop_data || [];
  const uniqueBuckets = Array.from(new Set(items.map(i => i.target_bucket).filter(Boolean))) as string[];
  const buckets = uniqueBuckets.length > 0 ? uniqueBuckets : ["Yes", "No"];

  // Determine if this is a simple Yes/No drag drop (Decision Making Style - Image 1)
  const isYesNo = buckets.length === 2 && buckets.every(b => b.toLowerCase() === "yes" || b.toLowerCase() === "no");

  const handleSetAnswer = (itemId: number, bucketName: string | null) => {
    if (isReviewMode) return;
    onAnswerChange(itemId, bucketName, !isYesNo);
  };

  if (isYesNo) {
    // --- DECISION MAKING LAYOUT (Image 1 Style) ---
    return (
      <div className="flex gap-10 select-none font-sans min-h-[400px]">
        {/* Left Column: Fixed Statements List */}
        <div className="flex-1 flex flex-col gap-4 max-w-[65%]">
          {items.map((item) => {
            const assignedBucket = dragDropAnswers[item.id];

            // Review Mode Logic
            let reviewStyle = {};
            let isCorrect = false;

            if (isReviewMode) {
              const target = item.target_bucket?.toLowerCase().trim();
              const actual = assignedBucket?.toLowerCase().trim();
              isCorrect = target === actual;

              if (isCorrect) {
                reviewStyle = { borderColor: "#22c55e", backgroundColor: "#f0fdf4" }; // Green-500, Green-50
              } else if (assignedBucket) {
                reviewStyle = { borderColor: "#ef4444", backgroundColor: "#fef2f2" }; // Red-500, Red-50
              } else {
                // Missed (Unanswered in review)
                reviewStyle = { borderColor: "#ef4444", backgroundColor: "#fff" };
              }
            }

            return (
              <div key={item.id} className="flex items-center gap-4">
                {/* Statement Box */}
                <div className="flex-1 min-h-[55px] bg-white border border-black p-4 flex items-center shadow-sm">
                  <span className="text-[15px] font-normal text-black leading-snug">
                    {item.item_text}
                  </span>
                </div>

                {/* Drop Zone */}
                <div
                  style={isReviewMode ? reviewStyle : undefined}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    const bucketName = e.dataTransfer.getData("bucketName");
                    if (bucketName) handleSetAnswer(item.id, bucketName);
                  }}
                  className={`w-[120px] h-[55px] border ${!isReviewMode ? "border-black" : ""} transition-colors flex items-center justify-center relative ${!isReviewMode
                    ? (assignedBucket ? "bg-white" : "bg-[#b3b3b3]")
                    : "" // Style handled by inline style
                    }`}
                >
                  <AnimatePresence mode="popLayout">
                    {assignedBucket && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="w-full h-full flex items-center justify-center relative px-2"
                      >
                        <span className="text-[15px] font-normal text-black">
                          {assignedBucket}
                        </span>
                        {!isReviewMode && (
                          <button
                            onClick={() => handleSetAnswer(item.id, null)}
                            className="absolute -top-2 -right-2 bg-white rounded-full text-red-500 shadow-sm border border-gray-100 p-0.5"
                          >
                            <XCircle size={16} color="black" />
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column: Draggable Labels Pool (Image 1 Box Style) */}
        <div className="w-[180px] flex flex-col gap-4 sticky top-0">
          <div className="p-4 bg-[#d9d9d9] border border-black flex flex-col gap-3 min-h-[150px] justify-center">
            {buckets.map((bucketName) => (
              <div
                key={bucketName}
                draggable={!isReviewMode && !isLocked}
                onDragStart={(e: any) => {
                  e.dataTransfer.setData("bucketName", bucketName);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => isLocked && onLockedClick?.()}
                title={isLocked ? "Scroll to view full content" : ""}
                className={`w-full h-[55px] bg-white border border-black flex items-center justify-center transition-colors shadow-sm ${!isLocked ? "cursor-grab active:cursor-grabbing hover:bg-gray-50" : "cursor-not-allowed"}`}
              >
                <span className="text-[15px] font-normal text-black">{bucketName}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded text-[13px] text-gray-500 italic">
            Drag the labels and drop them into the target boxes next to each statement.
          </div>
        </div>
      </div>
    );
  }

  // --- SJT / OTHER LAYOUT (Image 2 Style) ---
  const unassignedItems = items.filter(item => !dragDropAnswers[item.id]);

  return (
    <div className="flex flex-col gap-8 select-none font-sans min-h-[400px]">
      {/* Top Part: Targets and Drop Zones */}
      <div className="flex flex-col gap-6 max-w-4xl">
        {buckets.map((bucketName) => {
          const assignedItem = items.find(item => dragDropAnswers[item.id] === bucketName);

          // Review Mode Logic for Bucket Style
          let reviewStyle = {};
          let isCorrect = false;

          if (isReviewMode && assignedItem) {
            const target = assignedItem.target_bucket?.toLowerCase().trim();
            const actual = bucketName.toLowerCase().trim();
            isCorrect = target === actual;

            if (isCorrect) {
              reviewStyle = { borderColor: "#22c55e", backgroundColor: "#f0fdf4" };
            } else {
              reviewStyle = { borderColor: "#ef4444", backgroundColor: "#fef2f2" };
            }
          }


          return (
            <div key={bucketName} className="flex items-center gap-4">
              {/* Target Label */}
              <div className="w-[200px] h-[55px] bg-white border border-black flex items-center justify-center text-center px-4">
                <span className="text-[15px] font-normal text-black">{bucketName}</span>
              </div>

              {/* Drop Zone */}
              <div
                style={isReviewMode && assignedItem ? reviewStyle : undefined}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  const itemId = e.dataTransfer.getData("itemId");
                  if (itemId) handleSetAnswer(Number(itemId), bucketName);
                }}
                className={`flex-1 h-[55px] border ${!isReviewMode ? "border-black" : ""} transition-colors flex items-center justify-center relative ${!isReviewMode
                  ? (assignedItem ? "bg-white" : "bg-[#b3b3b3]")
                  : (assignedItem ? "" : "bg-[#b3b3b3]") // keep grey if empty in review
                  }`}
              >
                <AnimatePresence mode="popLayout">
                  {assignedItem && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className={`w-full h-full flex items-center justify-center relative px-4 ${!isLocked ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed"}`}
                      draggable={!isReviewMode && !isLocked}
                      onDragStart={(e: any) => {
                        e.dataTransfer.setData("itemId", assignedItem.id.toString());
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onClick={() => isLocked && onLockedClick?.()}
                      title={isLocked ? "Scroll to view full content" : ""}
                    >
                      <span className="text-[15px] font-medium text-black text-center line-clamp-2">
                        {assignedItem.item_text}
                      </span>
                      {!isReviewMode && (
                        <button
                          onClick={() => handleSetAnswer(assignedItem.id, null)}
                          className="absolute -top-2 -right-2 bg-white rounded-full text-red-500 shadow-sm border border-gray-100 p-0.5"
                        >
                          <XCircle size={16} color="black" />
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Part: Unassigned Items Pool (Large Grey Box) */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={(e) => {
          const itemId = e.dataTransfer.getData("itemId");
          if (itemId) handleSetAnswer(Number(itemId), null);
        }}
        className="p-8 bg-[#d9d9d9] border border-black min-h-[150px] flex flex-col gap-4"
      >
        <AnimatePresence mode="popLayout">
          {unassignedItems.map((item) => (
            <motion.div
              layout
              key={item.id}
              draggable={!isReviewMode && !isLocked}
              onDragStart={(e: any) => {
                if (isLocked) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.setData("itemId", item.id.toString());
                e.dataTransfer.effectAllowed = "move";
              }}
              onClick={() => isLocked && onLockedClick?.()}
              title={isLocked ? "Scroll to view full content" : ""}
              className={`bg-white border border-black p-4 transition-colors shadow-sm min-h-[55px] flex items-center justify-center text-center ${!isLocked ? "cursor-grab active:cursor-grabbing hover:bg-gray-50" : "cursor-not-allowed"}`}
            >
              <span className="text-[15px] font-medium text-black leading-snug">
                {item.item_text}
              </span>
            </motion.div>
          ))}
          {unassignedItems.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-gray-500 italic">
              All items have been assigned. Drag an item back here to unassign it.
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- Single Page Question (Full Width) ---
interface SinglePageQuestionProps {
  question: Question;
  selectedOptionIndex: number | null;
  dragDropAnswers?: Record<number, string | null>;
  writingAnswer?: string;
  passage?: string; // Add passage
  promptUrl?: string; // Add promptUrl
  onOptionSelect: (index: number) => void;
  onDragDropChange?: (itemId: number, bucketName: string | null, isExclusive?: boolean) => void;
  onWritingChange?: (value: string) => void;
  isReviewMode?: boolean;
  onScrollStatusChange?: (status: { left: boolean; right: boolean }) => void;
  onLockedClick?: () => void;
}

export const SinglePageQuestion: React.FC<SinglePageQuestionProps> = ({
  question,
  selectedOptionIndex,
  dragDropAnswers = {},
  writingAnswer = "",
  passage,
  promptUrl,
  onOptionSelect,
  onDragDropChange = () => { },
  onWritingChange = () => { },
  isReviewMode = false,
  onScrollStatusChange,
  onLockedClick,
}) => {
  // Debug: Log passage prop in SinglePageQuestion
  console.log("🔍 SinglePageQuestion Passage Debug:", {
    questionId: question.question_id,
    hasPassage: !!passage,
    passageLength: passage?.length || 0,
    passagePreview: passage?.substring(0, 100) + ((passage?.length ?? 0) > 100 ? "..." : ""),
    passageType: typeof passage,
    questionPrompt: question.prompt,
    questionInstruction: question.instruction
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasViewedBottom, setHasViewedBottom] = useState({
    left: true,
    right: true,
  });

  const checkScrollStatus = useCallback(() => {
    if (!onScrollStatusChange || !scrollRef.current) return;

    const buffer = 10;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // For single page, we treat it as the 'right' pane or just unified status
    // To satisfy the check in Practicetest (left && right), we set both to true if scrolled
    if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer) {
      if (!hasViewedBottom.right) {
        const newStatus = { left: true, right: true };
        setHasViewedBottom(newStatus);
        onScrollStatusChange(newStatus);
      }
    }
  }, [hasViewedBottom, onScrollStatusChange]);

  useEffect(() => {
    if (!onScrollStatusChange) return;

    const checkInitial = () => {
      if (scrollRef.current && scrollRef.current.scrollHeight > scrollRef.current.clientHeight + 5) {
        return { left: true, right: false }; // Need to scroll right pane
      }
      return { left: true, right: true };
    };

    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    const initialStatus = checkInitial();
    setHasViewedBottom(initialStatus);
    onScrollStatusChange(initialStatus);

    const timeoutId = setTimeout(() => {
      const recheckStatus = checkInitial();
      setHasViewedBottom(recheckStatus);
      onScrollStatusChange(recheckStatus);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [question.question_id, onScrollStatusChange]);

  const handleScroll = useCallback(() => {
    checkScrollStatus();
  }, [checkScrollStatus]);

  let adaptImageWidth = useMediaQuery("(min-width: 768px)")

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white min-h-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 p-4 overflow-y-auto ucat-scrollbar min-h-0"
      >
        <div className="max-w-full mx-auto">
          {/* Prompt Image (if provided) */}
          {promptUrl && (
            <div className="mb-6">
              <div className="prompt-img-wrapper" style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <img
                  src={promptUrl}
                  alt="Prompt"
                  className="resizable-image max-w-full border rounded"
                  style={{
                    width: `${question.prompt_imgWidth || 90}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Passage / Prompt (if provided) */}
          {passage && (
            <div
              className="mb-6 whitespace-pre-wrap text-[15px] font-[400] text-black leading-[17px]"
              dangerouslySetInnerHTML={{ __html: passage }}
            />
          )}

          {/* Question Text */}
          {question.question_text && (
            <div className="mb-6">
              <p
                className="text-[15px] font-[400] text-black leading-[17px]"
                dangerouslySetInnerHTML={{ __html: question.question_text }}
              />
            </div>
          )}

          {/* Question Image */}
          {question.question_url && (
            <div className="mb-6">
              <div className="prompt-img-wrapper" style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <img
                  src={question.question_url}
                  alt="Question"
                  className="resizable-image max-w-full border rounded"
                  style={{
                    width: `${adaptImageWidth ? (question?.imgWidth || 90) : 99}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Options / Input Component based on AnswerType */}
          <div className="ml-2">
            {question.answer_type === AnswerType.DRAG_DROP ? (
              <DragDropQuestion
                question={question}
                dragDropAnswers={dragDropAnswers || {}}
                onAnswerChange={onDragDropChange || (() => { })}
                isReviewMode={isReviewMode}
                isLocked={!hasViewedBottom.right}
                onLockedClick={onLockedClick}
              />
            ) : question.answer_type === "TRUE_FALSE" ? (
              <TrueFalseQuestion
                question={question}
                selectedOptionIndex={selectedOptionIndex}
                onOptionSelect={onOptionSelect}
                isReviewMode={isReviewMode}
              />
            ) : question.question_type_name === "Writing" || question.question_type_name === "Written" ? (
              <WritingQuestion
                value={writingAnswer}
                onChange={onWritingChange}
                isReviewMode={isReviewMode}
              />
            ) : (
              /* Default MCQ Style */
              <div className="space-y-4">
                {question.options.map((option, idx) => {
                  const label = String.fromCharCode(65 + idx);
                  const isSelected = selectedOptionIndex === idx;
                  const safeOptionText =
                    typeof option.option_text === "string"
                      ? option.option_text
                      : null;

                  return (
                    <div
                      key={option.option_id}
                      onClick={() => {
                        if (!isReviewMode) {
                          onOptionSelect(idx);
                        }
                      }}
                      className={`flex items-start mb-4 cursor-pointer ${isSelected ? "bg-[#FFFFFF]" : "bg-white"
                        }`}
                    >
                      <input
                        type="radio"
                        checked={isSelected}
                        disabled={isReviewMode}
                        onChange={() => onOptionSelect(idx)}
                        className="mt-[4px] mr-3 accent-[#005a9c]"
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                      />

                      <div className="flex-1" style={{ display: "flex", flexWrap: "nowrap" }}>
                        <span className="font-normal mr-[44px] text-[15px]">
                          {label}.
                        </span>
                        {safeOptionText && (
                          <div
                            className="text-[15px] font-normal"
                            dangerouslySetInnerHTML={{ __html: safeOptionText }}
                          />
                        )}

                        {option.option_url && (
                          <img
                            src={option.option_url}
                            alt={`Option ${label}`}
                            className="mt-2 max-w-[300px] border"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>


          {/* Review Mode - Show Answer */}
          {/* {isReviewMode && question.correctAnswerIndex !== undefined && (
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2 text-[#005a9c] font-bold">
                <MessageCircle size={18} />
                <span>Answer Explanation</span>
              </div>

              <p className="text-sm text-gray-800">
                <span className="text-[14px] text-white font-bold">
                  Correct Response:{" "}
                  {String.fromCharCode(65 + question.correctAnswerIndex)}
                </span>
              </p>

              {question.explanation && (
                <p className="text-[14px] text-white mt-2">
                  {question.explanation}
                </p>
              )}
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

// --- Split Pane (Passage & Question) ---
interface SplitPaneProps {
  passage?: string;
  promptUrl?: string; // Image prompt URL
  question: Question;
  selectedOptionIndex: number | null;
  dragDropAnswers?: Record<number, string | null>;
  writingAnswer?: string; // Added
  onOptionSelect: (index: number) => void;
  onDragDropChange?: (itemId: number, bucketName: string | null, isExclusive?: boolean) => void;
  onWritingChange?: (value: string) => void; // Added
  isReviewMode?: boolean;
  onScrollStatusChange: (status: { left: boolean; right: boolean }) => void;
  onLockedClick?: () => void;
}

export const SplitPane: React.FC<SplitPaneProps> = ({
  passage,
  promptUrl,
  question,
  selectedOptionIndex,
  dragDropAnswers = {},
  writingAnswer = "", // Added
  onOptionSelect,
  onDragDropChange = () => { },
  onWritingChange = () => { }, // Added
  isReviewMode = false,
  onScrollStatusChange,
  onLockedClick,
}) => {
  // Debug: Log passage prop in SplitPane
  console.log("🔍 SplitPane Passage Debug:", {
    questionId: question.question_id,
    hasPassage: !!passage,
    passageLength: passage?.length || 0,
    passagePreview: passage?.substring(0, 100) + ((passage?.length ?? 0) > 100 ? "..." : ""),
    passageType: typeof passage,
    questionPrompt: question.prompt,
    questionInstruction: question.instruction,
    hasPromptUrl: !!promptUrl
  });

  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  // Track if the user has reached the bottom at least once for current content
  const [hasViewedBottom, setHasViewedBottom] = useState({
    left: true,
    right: true,
  });

  // Stable callback for checking scroll status
  const checkScrollStatus = useCallback(() => {
    const buffer = 10; // 10px buffer
    let newLeft = hasViewedBottom.left;
    let newRight = hasViewedBottom.right;

    if (!newLeft && leftRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = leftRef.current;
      // Check if content fits in viewport OR user has scrolled to bottom
      if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer) {
        newLeft = true;
      }
    }

    if (!newRight && rightRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = rightRef.current;
      // Check if content fits in viewport OR user has scrolled to bottom
      if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer) {
        newRight = true;
      }
    }

    if (
      newLeft !== hasViewedBottom.left ||
      newRight !== hasViewedBottom.right
    ) {
      const newStatus = { left: newLeft, right: newRight };
      setHasViewedBottom(newStatus);
      onScrollStatusChange(newStatus);
    }
  }, [hasViewedBottom, onScrollStatusChange]);

  // Reset when question changes
  useEffect(() => {
    // Initial check function
    const checkInitial = () => {
      let l = true;
      let r = true;
      // If content is larger than viewport, then we haven't viewed bottom yet
      if (
        leftRef.current &&
        leftRef.current.scrollHeight > leftRef.current.clientHeight + 5 // Add small buffer
      ) {
        l = false;
      }
      if (
        rightRef.current &&
        rightRef.current.scrollHeight > rightRef.current.clientHeight + 5 // Add small buffer
      ) {
        r = false;
      }
      return { left: l, right: r };
    };

    // Reset scroll position to top
    if (leftRef.current) leftRef.current.scrollTop = 0;
    if (rightRef.current) rightRef.current.scrollTop = 0;

    const initialStatus = checkInitial();
    setHasViewedBottom(initialStatus);
    // Notify parent immediately
    onScrollStatusChange(initialStatus);

    // Re-check after a short delay to account for images/content loading
    const timeoutId = setTimeout(() => {
      const recheckStatus = checkInitial();
      setHasViewedBottom(recheckStatus);
      onScrollStatusChange(recheckStatus);
    }, 300);

    // Also check when images load
    const leftImages = leftRef.current?.querySelectorAll('img');
    const rightImages = rightRef.current?.querySelectorAll('img');
    const images = [
      ...Array.from(leftImages || []),
      ...Array.from(rightImages || []),
    ];

    const handleImageLoad = () => {
      const recheckStatus = checkInitial();
      setHasViewedBottom(recheckStatus);
      onScrollStatusChange(recheckStatus);
    };

    images.forEach(img => {
      if (img.complete) {
        handleImageLoad();
      } else {
        img.addEventListener('load', handleImageLoad);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      images.forEach(img => {
        img.removeEventListener('load', handleImageLoad);
      });
    };
  }, [question.question_id, onScrollStatusChange]);

  const handleScroll = useCallback(() => {
    checkScrollStatus();
  }, [checkScrollStatus]);

  // Also check on window resize
  useEffect(() => {
    window.addEventListener("resize", handleScroll);
    return () => window.removeEventListener("resize", handleScroll);
  }, [handleScroll]);

  let adaptImageWidth = useMediaQuery("(min-width: 768px)")
  document.addEventListener("focusin", (e) => {
    console.log("Focused:", e.target);
  });

  return (
    <div className="flex flex-col md:flex-row h-full overflow-hidden bg-white min-h-0">
      {/* Left Pane: Passage or Prompt Image */}

      <div
        className={`md:w-[60%] flex flex-col min-h-0 ${!passage && !promptUrl ? "hidden md:block md:invisible" : ""
          }`}
      >
        <div
          ref={leftRef}
          onScroll={handleScroll}
          className="flex-1 p-4 overflow-y-auto ucat-scrollbar min-h-0 text-gray-900 leading-[17px] font-sans text-[15px]"
        >
          {/* Show prompt image if available */}
          {promptUrl && (
            <div className="prompt-img-wrapper" style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <img
                src={promptUrl}
                alt="Prompt"
                className="resizable-image max-w-full border rounded"
                style={{
                  width: `${question.prompt_imgWidth || 90}%`,
                }}
              />
            </div>
          )}

          {/* Show text passage if available */}
          {passage && (
            <div
              className="whitespace-pre-wrap mb-4"
              style={{ fontSize: "14.6px" }}
              dangerouslySetInnerHTML={{ __html: passage }}
            />
          )}
        </div>
      </div>

      {/* Vertical Divider */}
      <div className="hidden md:block w-1.5 bg-[#005a9c] flex-shrink-0"></div>

      {/* Right Pane: Question & Options */}
      <div className="md:w-[40%] flex flex-col h-full min-h-0">
        <div
          ref={rightRef}
          onScroll={handleScroll}
          className="flex-1 p-4 overflow-y-auto ucat-scrollbar min-h-0"
        >
          {/* Question Text and Image */}
          <div className="mb-6">
            {question.question_text && (
              <p
                className="text-[15px] font-normal text-black leading-[17px] mb-4"
                dangerouslySetInnerHTML={{ __html: question.question_text }}
              />
            )}
            {question.question_url && (
              <div className="prompt-img-wrapper" style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <img
                  src={question.question_url}
                  alt="Question"
                  className="resizable-image max-w-full border rounded mb-4"
                  style={{
                    width: `${adaptImageWidth ? (question?.imgWidth || 90) : 99}%`,
                  }}
                />
              </div>
            )}
          </div>

          {/* Options / Input Component based on AnswerType */}
          <div className="ml-2">
            {question.answer_type === AnswerType.DRAG_DROP ? (
              <DragDropQuestion
                question={question}
                dragDropAnswers={dragDropAnswers || {}}
                onAnswerChange={onDragDropChange || (() => { })}
                isReviewMode={isReviewMode}
                isLocked={!hasViewedBottom.left || !hasViewedBottom.right}
                onLockedClick={onLockedClick}
              />
            ) : question.answer_type === "TRUE_FALSE" ? (
              <TrueFalseQuestion
                question={question}
                selectedOptionIndex={selectedOptionIndex}
                onOptionSelect={onOptionSelect}
                isReviewMode={isReviewMode}
              />
            ) : question.question_type_name === "Writing" || question.question_type_name === "Written" ? (
              <WritingQuestion
                value={writingAnswer}
                onChange={onWritingChange}
                isReviewMode={isReviewMode}
              />
            ) : (
              /* Default MCQ Style */
              <div className="space-y-4">
                {question.options.map((option, idx) => {
                  const label = String.fromCharCode(65 + idx);
                  const isSelected = selectedOptionIndex === idx;

                  return (
                    <div
                      key={option.option_id}
                      onClick={() => {
                        if (!isReviewMode) {
                          onOptionSelect(idx);
                        }
                      }}
                      className={`flex items-start mb-4 cursor-pointer ${isSelected ? "bg-[#FFFFFF]" : "bg-white"
                        }`}
                    >
                        <input
                          type="radio"
                          checked={isSelected}
                          disabled={isReviewMode}
                          onChange={() => onOptionSelect(idx)}
                          className="mt-[4px] mr-3 accent-[#005a9c]"
                          onClick={(e) =>{ e.stopPropagation();
                            e.currentTarget.blur();
                          }}
                          tabIndex={-1}
                        />

                      <div className="flex-1" style={{ display: "flex", flexWrap: "nowrap" }}>
                        <span className="font-normal mr-[44px] text-[15px]" >
                          {label}.
                        </span>
                        {option.option_text && (
                          <div
                            className="text-[15px]"
                            style={{ lineHeight: "normal", marginTop: "2px" }}
                            dangerouslySetInnerHTML={{ __html: option.option_text.trim() }}
                          />
                        )}
                        {option.option_url && (
                          <img
                            src={option.option_url}
                            alt={`Option ${label}`}
                            className="mt-2 max-w-[300px] border"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>


          {/* {isReviewMode && question.correctAnswerIndex !== undefined && (
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-2 mb-2 text-[#005a9c] font-bold">
                <MessageCircle size={18} />
                <span>Answer Explanation</span>
              </div>

              <p className="text-sm text-gray-800">
                <span className="text-[14px] text-white font-bold">
                  Correct Response:{" "}
                  {String.fromCharCode(65 + question.correctAnswerIndex)}
                </span>
              </p>

              {question.explanation && (
                <p className="text-[14px] text-white mt-2">
                  {question.explanation}
                </p>
              )}
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

// --- Explanation Modal ---
interface ExplanationModalProps {
  isOpen: boolean;
  onClose: () => void;
  chosenResponseIndex?: number;
  correctAnswerIndex?: number;
  explanation?: string;
}

export const ExplanationModal: React.FC<ExplanationModalProps> = ({
  isOpen,
  onClose,
  correctAnswerIndex,
  explanation,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title="Answer Explanation"
    width="max-w-2xl"
    isDraggable={true}
    showBackdropBlur={false}
  >
    <div className="text-sm ">
      {correctAnswerIndex !== undefined && correctAnswerIndex >= 0 && (
        <p className=" text-[14px] text-black font-bold mb-2">
          Correct response: {String.fromCharCode(65 + correctAnswerIndex)}
        </p>
      )}
      <div
        className="text-[14px] text-black"
        dangerouslySetInnerHTML={{ __html: explanation || "" }}
      />
    </div>
  </Modal>
);

// --- Submit Exam Modal ---
interface SubmitExamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  unansweredCount: number;
  flaggedCount: number;
}

export const SubmitExamModal: React.FC<SubmitExamModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  unansweredCount,
  flaggedCount,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Submit Exam" width="max-w-3xl">
    <div className="text-sm p-4">
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3 text-orange-600 bg-orange-50 p-3 rounded border border-orange-100 justify-center">
          <img src={InfoIcon} style={{ height: "100%", width: "100%" }} alt="i" />
          <div>
            <p className="font-bold">Unanswered Questions</p>
            <p>You have {unansweredCount} unanswered question{unansweredCount !== 1 ? 's' : ''}.</p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-blue-600 bg-blue-50 p-3 rounded border border-blue-100">
          <Flag size={20} className={flaggedCount > 0 ? "fill-blue-600" : ""} />
          <div>
            <p className="font-bold">Flagged Questions</p>
            <p>{flaggedCount > 0 ? `You have ${flaggedCount} flagged question${flaggedCount !== 1 ? 's' : ''}.` : "No questions have been flagged."}</p>
          </div>
        </div>
      </div>

      <p className="mb-6 text-[white] text-[16px] font-bold text-center">
        Once you submit this section, you will not be able to come here again.
      </p>

      <div className="flex justify-center items-center gap-4   ">
        <button
          onClick={onClose}
          className="w-40 px-2 py-3 bg-[#4678BE] text-[white] border border-[#cfd7df] rounded font-semibold text-sm shadow-sm hover:bg-[#004f86]"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          Return to Test
        </button>

        <button
          onClick={onConfirm}
          className="w-40 px-2 py-3 bg-[#4678BE] text-[white] text-[#005a9c] rounded font-bold text-sm shadow-md hover:bg-[#004f86]"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {unansweredCount === 0 && flaggedCount === 0 ? "Submit" : "Submit Anyway"}
        </button>
      </div>
    </div>
  </Modal>
);

// --- q Confirmation Modal ---
interface EndExamConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const EndExamConfirmationModal: React.FC<EndExamConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => (
  <EndReviewConfirmationModal
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    incompleteCount={0}
    isEndExam={true}
  />
);



// --- End Review Confirmation Modal ---
interface EndReviewConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  incompleteCount: number;
  isEndExam?: boolean;
  reConfirm?: boolean;
}

export const EndReviewConfirmationModal: React.FC<EndReviewConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  incompleteCount,
  isEndExam = false,
  reConfirm: parentReConfirm = false
}) => {
  const [activeReConfirm, setActiveReConfirm] = React.useState(false);

  // Reset internal state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setActiveReConfirm(false);
    }
  }, [isOpen]);

  const handleYes = React.useCallback(() => {
    if (isEndExam) {
      onConfirm();
    } else {
      if (activeReConfirm) {
        onConfirm();
      } else {
        setActiveReConfirm(true);
      }
    }
  }, [isEndExam, activeReConfirm, onConfirm]);

  const handleNo = React.useCallback(() => {
    onClose();
  }, [onClose]);

  // Keyboard shortcuts
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) {
        const key = e.key.toLowerCase();
        if (key === 'y') {
          e.preventDefault();
          handleYes();
        } else if (key === 'n') {
          e.preventDefault();
          handleNo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleYes, handleNo]);

  if (!isOpen) return null;

  return (
    <div className="ucat-module fixed inset-0 flex items-center justify-center z-[9999] bg-black/40">
      <div className="bg-[#006DAA] w-[55%] max-w-[500px] shadow-2xl">
        <div className="text-white px-3 py-3 flex items-center justify-between f-tahoma">
          <h2 className="text-[16px] f-tahoma">{!activeReConfirm ? "End Review" : "End Review Confirmation"}</h2>
        </div>
        <div className="h-[1px] bg-white w-full"></div>

        <div className="p-4 text-white">
          <div className="flex items-start gap-3 mb-6">
            <div className="flex-shrink-0">
              <div className="rounded-sm h-[32px] w-[32px]">
                <img src={AlertIcon} style={{ height: "100%", width: "100%" }} alt="!" />
              </div>
            </div>
            <div className="text-[16px] f-tahoma leading-snug mt-1">
              {!isEndExam ? (
                <>
                  <p className="mb-4 text-[16px] f-tahoma">
                    {!activeReConfirm ? `You have chosen to end the current review.${incompleteCount > 0 ? ` You still have ${incompleteCount} incomplete question${incompleteCount !== 1 ? 's' : ''}.` : ""} If you click Yes, you will NOT be able to return to this review.`
                      : `Please confirm that you want to end this review. If you click Yes, you will NOT be able to return to this review and answer the ${incompleteCount} questions you have not completed.`}
                  </p>
                  <p className="text-[16px] f-tahoma">Are you sure you want to end this review?</p>
                </>
              ) : (
                <>
                  <p className="mb-4 text-[16px] f-tahoma">
                    You have chosen to end the exam. If you click Yes, you will NOT be able to return to the exam.
                  </p>
                  <p className="text-[16px] f-tahoma">Are you sure you want to end this exam?</p>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={handleYes}
              className="px-1 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[60px]"
            >
              <span className="underline" style={{ fontSize: "16px" }}>Y</span>es
            </button>
            <button
              onClick={handleNo}
              className="px-1 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[60px]"
            >
              <span className="underline" style={{ fontSize: "16px" }}>N</span>o
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// --- Time Expired Modal ---
interface TimeExpiredModalProps {
  isOpen: boolean;
  onConfirm: () => void;
}

export const TimeExpiredModal: React.FC<TimeExpiredModalProps> = ({
  isOpen,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ucat-module fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
      <div className="bg-[#006DAA] w-[55%] max-w-lg mx-1 border border-white shadow-2xl">
        <div className="text-white px-3 py-2 border-b border-[#FFF] f-tahoma">
          <h2 className="text-[16px] f-tahoma">Time Expired</h2>
        </div>

        <div className="p-3 text-white f-tahoma">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 mt-1 h-[32px] w-[32px]" style={{ borderRadius: "50%" }}>
              <img src={InfoIcon} style={{ height: "100%", width: "100%" }} alt="i" />
            </div>
            <div className="text-[16px] leading-[1.4] pr-4">
              <p className="f-tahoma text-[16px]">
                Your time on this section has expired. Timing has begun on the next section. Click OK to continue.
              </p>
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <button
              onClick={onConfirm}
              className="px-3 py-1 text-[#FFF] font-normal border border-white hover:text-[#FFFF00] transition-colors text-[16px] f-tahoma"
            >
              <span className="underline f-tahoma text-[16px] text-[#FFF]" style={{ fontSize: "16px" }}>O</span>K
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// --- Scroll Warning Modal ---
interface ScrollWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ScrollWarningModal: React.FC<ScrollWarningModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ucat-module fixed inset-0 flex items-center justify-center z-[20000] bg-black/40">

      <div className="bg-white w-[55%] overflow-hidden shadow-lg">
        {/* Title Bar */}
        <div className="bg-[#006DAA] text-white px-3 py-[10px] flex items-center justify-between">
          <h2 className="text-[16px] font-normal f-tahoma">Unseen Content</h2>
          {/* <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button> */}
          {/* commented for compliance */}
        </div>

        {/* Thin horizontal divider under title */}
        <div className="h-px bg-white/20" />

        {/* Content: icon + text, with OK button centered below */}
        <div className="bg-[#006DAA] text-white p-3">
          <div className="flex items-start gap-1">
            <div className="flex-shrink-0 bg-[#4a9ec9] rounded-full h-[32px] w-[32px]  flex items-center justify-center ml-4">
              <img src={InfoIcon} style={{ height: "100%", width: "100%" }} alt="i" />
            </div>

            <div className="text-[16px] leading-normal text-center f-tahoma">
              You have not yet viewed the entire screen. Make sure you view all multimedia content, select every tab and scroll to every corner.
            </div>
          </div>

          <div className="mt-1 flex justify-center">
            <button
              onClick={onClose}
              className="px-3 py-[4px] bg-transparent text-white font-normal text-[16px] border-1 border-white hover:#FF0 transition-colors"
            >
              <u className="text-white font-normal text-[16px] hover:#FF0">O</u>K
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Reload Warning Modal ---
interface ReloadWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const ReloadWarningModal: React.FC<ReloadWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ucat-module fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
      <div className="bg-[#006DAA] w-[55%] max-w-[500px] shadow-2xl">
        <div className="text-white px-3 py-3 flex items-center justify-between f-tahoma">
          <h2 className="text-[16px] f-tahoma">Warning</h2>
        </div>
        <div className="h-[1px] bg-white w-full"></div>

        <div className="p-4 text-white f-tahoma">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0">
              <div className="bg-[#006daa] rounded-sm p-1 h-[32px] w-[32px]">
                {/* <AlertTriangle size={32} className="text-[#005a9c]" /> */}
                <img src={AlertIcon} style={{ height: "100%", width: "100%" }} alt="!" />
              </div>
            </div>
            <div className="text-[16px] leading-[17px] mt-1">
              <p className="font-bold mb-2 text-[16px] f-tahoma">Unsaved Changes</p>
              <p className="text-[16px] f-tahoma">
                Leaving this page may result in lost progress. Are you sure you want to reload/leave?
              </p>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={onConfirm}
              className="px-2 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[80px]"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : (
                <span className="text-[16px]">
                  <span className="underline text-[16px] f-tahoma">L</span>eave
                </span>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[80px]"
            >
              <span className="underline text-[16px] f-tahoma">C</span>ancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- No Flagged Questions Modal ---
interface NoFlaggedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NoFlaggedModal: React.FC<NoFlaggedModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="ucat-module fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
      <div className="bg-[#006DAA] w-[55%] max-w-[500px] shadow-2xl">
        <div className="text-white px-3 py-3 flex items-center justify-between f-tahoma">
          <h2 className="text-[16px] f-tahoma">No Questions Flagged</h2>
        </div>
        <div className="h-[1px] bg-white w-full"></div>

        <div className="p-4 text-white f-tahoma">
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0">
              <div className="bg-[#FFFFFF44] rounded-sm h-[32px] w-[32px]" style={{ borderRadius: "50%" }}>
                <img src={InfoIcon} style={{ height: "100%", width: "100%" }} alt="i" />
              </div>
            </div>
            <div className="text-[16px] leading-[17px] mt-1">
              <p className="text-[16px] f-tahoma">
                No questions have been flagged for review. Please make your selections and try again.
              </p>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={onClose}
              className="px-2 py-1 bg-transparent text-white font-normal border border-white hover:bg-[#00478b] text-[16px] min-w-[50px]"
            >
              <span className="underline text-[16px] f-tahoma">O</span>K
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Section Review Screen ---
interface SectionReviewProps {
  sectionTitle: string;
  questions: Question[];
  answers: Record<string, UserAnswer>;
  isLastSection?: boolean;
  onJump: (index: number) => void;
  onReviewAll: () => void;
  onReviewIncomplete: () => void;
  onReviewFlagged: () => void;
  onEndReview: () => void;
  showEndReview?: boolean;
  onEndReviewConfirm?: () => void;
  onEndReviewCancel?: () => void;
  showInstructions: boolean;
  setShowInstructions: (show: boolean) => void;
  onToggleFlag?: (questionId: number) => void;
}

export const UcatSectionReview: React.FC<SectionReviewProps> = ({
  sectionTitle,
  questions,
  answers,
  isLastSection = false,
  onJump,
  onReviewAll,
  onReviewIncomplete,
  onReviewFlagged,
  onEndReview,
  showEndReview = false,
  onEndReviewConfirm,
  onEndReviewCancel,
  showInstructions,
  setShowInstructions,
  onToggleFlag,
}) => {
  console.log("review question", questions)
  const incompleteCount = questions.filter(q => {
    const ans = answers[q.question_id];
    const isMcqAnswered = ans && (ans.selectedOptionIndex !== null && ans.selectedOptionIndex !== undefined);
    const isDragDropAnswered = ans && ans.dragDropAnswers && Object.values(ans.dragDropAnswers).some(val => val !== null);
    const isOtherAnswered = ans && (
      (ans.sentence && ans.sentence.trim() !== "" && ans.sentence !== "null" && ans.sentence !== "{}") ||
      (ans.answer_data && ans.answer_data.trim() !== "" && ans.answer_data !== "null" && ans.answer_data !== "{}")
    );
    return !isMcqAnswered && !isDragDropAnswered && !isOtherAnswered;
  }).length;

  // Keyboard shortcuts for review screen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If instructions are open, block other shortcuts
      if (showInstructions) {
        if (e.key === 'Escape') {
          setShowInstructions(false);
          e.preventDefault();
        }
        return;
      }

      if (e.altKey) {
        const key = e.key.toLowerCase();
        e.preventDefault();

        switch (key) {
          case 'e':
            onEndReview();
            break;
          case 'a':
            onReviewAll();
            break;
          case 'i':
            onReviewIncomplete();
            break;
          case 'v':
            onReviewFlagged();
            break;
          case 'l':
            setShowInstructions(true);
            break;
          case 'y':
            if (showEndReview && onEndReviewConfirm) {
              onEndReviewConfirm();
            }
            break;
          case 'n':
            if (showEndReview && onEndReviewCancel) {
              onEndReviewCancel();
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onJump, onReviewAll, onReviewIncomplete, onReviewFlagged, onEndReview, showInstructions, showEndReview, onEndReviewConfirm, onEndReviewCancel]);

  return (
    <div className="flex flex-col h-full bg-white select-none overflow-hidden min-h-0 border-t border-gray-300">
      {/* Sub-header instruction bar */}
      <div className="p-1 flex items-center gap-2 text-[13px] text-black">
        {/* <div className="flex items-center gap-1 ml-2">
          <MessageCircle size={14} className="text-blue-700" />
          <span className="font-bold">Instructions</span>
        </div> */}
        <div className="flex-1 text-center text-[19px] font-[400] pr-10 mb-[10px] f-tahoma">
          {sectionTitle || ""} Review Prescore: use this screen to review the items and amend your responses
        </div>
      </div>

      {/* Section Title Bar */}
      <div className="bg-[#4678BE] text-white p-1.5 px-3 flex justify-between items-center text-[14px] font-bold f-tahoma">
        <div className="flex items-center gap-2 text-[18px] font-[400] f-tahoma">
          <div className=" h-4 text-[19px] border border-white flex items-center justify-center  leading-tight">−</div>
          Practice Test - {sectionTitle}
        </div>
        <div className="text-[19px] font-[400] f-tahoma">( {incompleteCount} Unseen/Incomplete )</div>
      </div>

      {/* Table Content */}
      <div className="flex-1 overflow-y-auto ucat-scrollbar min-h-0 bg-white">
        <table className="w-full border-collapse text-[14px]">
          <tbody>
            {questions.map((q, idx) => {
              const ans = answers[q.question_id];
              const isAnswered =
                (typeof ans?.selectedOptionIndex === "number" && ans.selectedOptionIndex >= 0) ||
                (ans?.dragDropAnswers && Object.values(ans.dragDropAnswers).some(val => val !== null)) ||
                (ans?.sentence && ans.sentence.trim() !== "" && ans.sentence !== "null" && ans.sentence !== "{}") ||
                (ans?.answer_data && ans.answer_data.trim() !== "" && ans.answer_data !== "null" && ans.answer_data !== "{}");
              const isViewed = ans?.isViewed || false;
              const isFlagged = ans?.isFlagged || false;

              let statusText = "Unseen";
              let statusColor = "text-red-600";
              if (isAnswered) {
                statusText = "Complete";
                statusColor = "text-black";
              } else if (isViewed) {
                statusText = "Incomplete";
                statusColor = "text-red-600";
              }

              return (
                <tr
                  key={q.question_id}
                  className={`border-b border-gray-100 hover:bg-[#FFFF00]/20 cursor-pointer group ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/30"} f-tahoma`}
                  onClick={() => onJump(idx)}
                >
                  <td className="py-1 px-4 flex items-left gap-3 font-normal text-[16px]">
                    <div
                      className="cursor-pointer p-1 hover:bg-gray-200 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onToggleFlag) onToggleFlag(q.question_id);
                      }}
                    >
                      <Flag
                        size={18}
                        className={`${isFlagged ? "fill-[#4678BE] text-[#4678BE]" : "text-[#4678BE] opacity-60"} hover:opacity-100`}
                      />
                    </div>
                    Question {idx + 1}
                  </td>
                  <td className={`py-1 px-4 font-normal text-[16px] text-left ${statusColor}`}>
                    {statusText}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Actions@@@@ */}
      <div className="bg-[#006DAA] p-[2px] flex justify-between items-center h-[35px] mt-auto">
        <button
          onClick={onEndReview}
          className="bg-transparent text-white h-full px-3 hover:bg-white/10 flex items-center gap-2 font-normal text-[16px] border-r-[2px] rounded-[2px]"
        >
          <LogOut size={22} strokeWidth={3} />
          {isLastSection ? (
            <span className="flex items-center !text-[19px] !font-[400] leading-[25px]">
              <span className="relative inline-block !text-[19px] !font-[400]">
                E
                <span className="absolute left-0 right-0 bottom-[2px] h-[1.5px] bg-white" />
              </span>
              nd Exam
            </span>
          ) : (
            <span className="flex items-center !text-[19px] !font-[400] leading-[25px]">
              <span className="relative inline-block !text-[19px] !font-[400]">
                E
                <span className="absolute left-0 right-0 bottom-[2px] h-[1.5px] bg-white" />
              </span>
              nd Review
            </span>
          )}
        </button>

        <div className="flex h-full">
          <button
            onClick={onReviewAll}
            className="bg-transparent text-white text-[19px] font-normal h-full px-4 border-l-[2px] border-white hover:bg-white/10 flex items-center gap-1 rounded-[2px]"
          >
            <CheckCircle size={18} className="mr-0.5" />
            <span className="text-[19px]">Review <span className="underline decoration-1 underline-offset-2 text-[19px]">A</span>ll</span>
          </button>
          <button
            onClick={onReviewIncomplete}
            className="bg-transparent text-white text-[19px] font-normal h-full px-4 border-l-[2px] border-white hover:bg-white/10 flex items-center gap-1 rounded-[2px]"
          >
            <XCircle size={18} className="mr-0.5" />
            <span className="text-[19px]">Review <span className="underline decoration-1 underline-offset-2 text-[19px]">I</span>ncomplete</span>
          </button>
          <button
            onClick={onReviewFlagged}
            className="bg-transparent text-white text-[19px] font-normal h-full px-4 border-l-[2px] border-white hover:bg-white/10 flex items-center gap-1 rounded-[2px]"
          >
            <Flag size={18} className="fill-white mr-0.5" />
            <span className="text-[19px]">Re<span className="underline decoration-1 underline-offset-2 text-[19px]">v</span>iew Flagged</span>
          </button>
        </div>
      </div>

      {/* Instructions Modal - Pearson Style */}
      {showInstructions && (
        <div className="ucat-module fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
          <div className="bg-[#006DAA] w-full max-w-5xl border border-white shadow-2xl overflow-hidden">
            {/* Title Bar */}
            <div className="text-white px-3 py-2 border-b border-[#FFF] flex items-center f-tahoma">
              <h2 className="text-[16px] font-normal">Review Instructions</h2>
            </div>

            <div className="p-4 text-white">
              <div className="flex items-start gap-8 mb-4">
                {/* Info Icon */}
                <div className="flex-shrink-0 mt-1">
                  <InfoSvg className="w-[38px] h-[48px] text-[#005a9c] fill-white" />
                </div>

                <div className="text-[16px] leading-[18px] space-y-6">
                  <p className="text-[16px] f-tahoma">
                    Below is a summary of your answers. You can review your questions in three (3) different ways.
                  </p>

                  <div>
                    <p className="mb-4 text-[16px] f-tahoma">
                      The buttons in the lower right-hand corner correspond to these choices:
                    </p>
                    <div className="space-y-1 ml-1">
                      <p className="text-[16px] f-tahoma">1. Review all of your questions and answers.</p>
                      <p className="text-[16px] f-tahoma">2. Review questions that are incomplete.</p>
                      <p className="text-[16px] f-tahoma">3. Review questions that are flagged for review. (Click the 'flag' icon to change the flag for review status.)</p>
                    </div>
                  </div>

                  <p className="f-tahoma text-[16px]">
                    You may also click on a question number to link directly to its location in the exam.
                  </p>
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-center mt-12 mb-2">
                <button
                  onClick={() => setShowInstructions(false)}
                  className="px-2 py-1 border border-white text-white font-normal hover:text-[#005a9c] transition-colors text-[16px]"
                >
                  <span className="f-tahoma underline text-[16px]">O</span>K
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
